import { Injectable } from '@angular/core';
import { SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { ApolloQueryResult } from 'apollo-client';
import { Logger } from 'aws-amplify';
import { AmplifyService } from 'aws-amplify-angular';
import { saveAs } from 'file-saver';
import gql from 'graphql-tag';
import { Observable, forkJoin, from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { AppSyncService } from 'src/app/core/appsync.service';
import { environment } from 'src/environments/environment';
import { getObjectS3PreSignedUrl, publicUploadS3PreSignedUrl } from 'src/graphql/queries';
import { v4 as uuid } from 'uuid';
import {
  DummyS3ObjectQueryQuery,
  GetObjectS3PreSignedUrlQuery,
  PublicUploadS3PreSignedUrlQuery
} from '../../../API';
import { LoadingSpinnerService } from '../loading-spinner/loading-spinner.service';
import { ContentType, ImageToDataUriPipe } from '../shared-pipes/image-to-data-uri.pipe';
import { S3ObjectInput } from './../../../API';
import { Attachment } from './attachments.component';

export interface PublicS3PreSignedUrlConfig {
  type: PublicS3PreSignedUrlType;
  config: any;
}

export interface GetObjectConfig extends PublicS3PreSignedUrlConfig {
  type: PublicS3PreSignedUrlType.getObject;
  config: S3ObjectInput;
}

export interface PutObjectConfig extends PublicS3PreSignedUrlConfig {
  type: PublicS3PreSignedUrlType.putObject;
  config: {
    filename: string;
    clinicId: string;
    contentType: string;
  };
}

export enum PublicS3PreSignedUrlType {
  getObject = 'getObject',
  putObject = 'putObject'
}

export type S3Object = DummyS3ObjectQueryQuery['dummyS3ObjectQuery'];
export type UrlWithContentType = {
  url: string | SafeResourceUrl | SafeUrl;
  contentType: ContentType;
  fileName?: string;
};

@Injectable({
  providedIn: 'root'
})
export class S3AttachmentService {
  private logger: Logger = new Logger('S3AttachmentService');
  constructor(
    private amplifyService: AmplifyService,
    private loadingSpinnerService: LoadingSpinnerService,
    private appSyncService: AppSyncService,
    private imageToDataUriPipe: ImageToDataUriPipe
  ) {}

  public getPresignedUrl(s3Object: S3Object): Promise<string> {
    // e.g. protected/us-east-1:bbe8a7e4-f083-469b-bd59-a44e0c8421f5/a5f4446d-5d79-44ce-8ca1-d87c671d2ff6.png
    const options = this.getS3DownloadOptions(s3Object.key);

    // See https://github.com/aws-amplify/amplify-js/issues/1912 for why I'm doing
    // typecast
    return this.amplifyService.storage().get(options.key, {
      bucket: s3Object.bucket,
      region: s3Object.region,
      ...options
    }) as Promise<string>;
  }

  private getS3Object(s3Object: S3Object, download = true): Observable<any> {
    const isViaPublicS3PreSignedUrl = JSON.parse(s3Object.metadata || '{}').viaPublicS3PreSignedUrl;
    return isViaPublicS3PreSignedUrl
      ? this.getObjectUsingPreSignedUrl(s3Object)
      : this.getObjectUsingBucket(s3Object, download);
  }

  private getObjectUsingBucket(s3Object: S3Object, download): Observable<any> {
    // e.g. protected/us-east-1:bbe8a7e4-f083-469b-bd59-a44e0c8421f5/a5f4446d-5d79-44ce-8ca1-d87c671d2ff6.png
    const options = this.getS3DownloadOptions(s3Object.key);
    return from(
      this.amplifyService.storage().get(options.key, {
        bucket: s3Object.bucket,
        region: s3Object.region,
        download: download,
        ...options
      })
    );
  }

  // Should probably use ContentDisposition instead of FileSaver. But that's not supported out of the box.
  // See https://github.com/aws-amplify/amplify-js/issues/2052 and https://github.com/awslabs/aws-mobile-appsync-sdk-js/issues/287
  public download(s3Object: S3Object) {
    this.loadingSpinnerService.show();
    this.getS3Object(s3Object).subscribe(value => {
      this.loadingSpinnerService.hide();
      saveAs(new Blob([value.Body], { type: value.ContentType }), this.getFileName(s3Object));
    });
  }

  public s3ObjectToUri(s3Object: S3Object): Observable<UrlWithContentType> {
    this.logger.debug('getS3ImageDataUri');

    return this.getS3Object(s3Object).pipe(
      catchError(e => {
        console.log(e);
        return of(null);
      }),
      switchMap(imageData =>
        imageData
          ? this.imageToDataUriPipe.transform(imageData.Body, imageData.ContentType, true).pipe(
              map(url => {
                return { url: url, contentType: imageData.ContentType };
              })
            )
          : of(null)
      )
    );
  }

  private getPresignedUrlForGetObject(s3Object: S3Object): Observable<any> {
    const { __typename, ...s3ObjectInput } = s3Object;
    const variables = {
      ...s3ObjectInput
    };
    return this.getGetObjectS3PreSignedUrl(variables).pipe(
      catchError(err => {
        console.error(`Failed to get URL for ${s3Object.fileName || 'file'}:`, err);
        return of(null);
      })
    );
  }

  private getObjectUsingPreSignedUrl(
    s3Object: S3Object
  ): Observable<{ Body: Blob; ContentType: string }> {
    return this.getPresignedUrlForGetObject(s3Object).pipe(
      mergeMap(publicS3PreSignedUrlResponse => {
        if (publicS3PreSignedUrlResponse.statusCode !== 200) {
          throw new Error(`HTTP ${publicS3PreSignedUrlResponse.status}`);
        }
        return from(
          fetch(publicS3PreSignedUrlResponse.url, {
            method: 'Get'
          })
        );
      }),
      mergeMap(s3ObjectResponse => {
        return from(s3ObjectResponse.blob());
      }),
      map(blob => ({
        Body: blob,
        ContentType: blob.type
      }))
    );
  }

  private getS3DownloadOptions(key: string) {
    const match: RegExpMatchArray = /([^\/]+)\/([^\/]+)\/(.*)/.exec(key);
    if (match.length === 4) {
      return {
        level: match[1],
        identityId: match[2],
        key: match[3],
        expires: 5 * 60
      };
    } else {
      throw new Error('S3Object key does not match expected pattern');
    }
  }

  public getFileName(s3Object: S3Object): string {
    if (s3Object) {
      return s3Object.fileName;
    } else {
      const match: RegExpMatchArray = /.*\/(.*)/.exec(s3Object.key);
      return match.length === 2 ? match[1] : null;
    }
  }

  public convertToS3ObjectInput(identityId: string, attachment: Attachment) {
    if (attachment instanceof File) {
      const file: File = attachment as File;
      const extension: string = /(?:\.([^.]+))?$/.exec(file.name)[0];

      const s3ObjectInput = {
        bucket: environment.userUploads.bucket,
        region: environment.userUploads.region,
        // @ts-ignore - appsync client accepts localUri as type of "File" but GraphQL does not have "File" scalar
        localUri: file as string,
        mimeType: file.type,
        fileName: file.name,
        key: 'protected/' + identityId + '/' + uuid() + extension
      };

      if (attachment['metadata']) {
        s3ObjectInput['metadata'] = attachment['metadata'];
      }

      return s3ObjectInput;
    } else {
      const s3object: S3Object = attachment as S3Object;
      delete s3object.__typename;
      return s3object as S3ObjectInput;
    }
  }

  uploadFiles(files: File[], clinicId: string): Observable<Attachment[]> {
    return this.getPresignedUrlsForPutObject(files, clinicId).pipe(
      switchMap(results => {
        const uploads = files
          .map((file, index) => ({
            file,
            data: results[index]
          }))
          .filter(item => item.data['url']);

        return forkJoin(
          uploads.map(item =>
            this.uploadToS3(item.file, item.data.url!).pipe(
              map(() => item.data.bucketInfo as Attachment),
              catchError(err => {
                console.error(`Upload failed for ${item.file.name}:`, err);
                return of(item.data.bucketInfo as Attachment);
              })
            )
          )
        );
      })
    );
  }

  private uploadToS3(file: File, url: string): Observable<void> {
    return from(
      fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      })
    ).pipe(
      switchMap(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return of(undefined);
      })
    );
  }

  getPresignedUrlsForPutObject(files: File[], clinicId: string): Observable<any[]> {
    const urlRequests = files.map(file => {
      const config = {
        clinicId: clinicId,
        filename: file.name,
        contentType: file.type
      };
      return this.getPublicUploadS3PreSignedUrl(config).pipe(
        catchError(err => {
          console.error(`Failed to get URL for ${file.name}:`, err);
          return of(null);
        })
      );
    });

    return forkJoin(urlRequests).pipe(map(urls => urls.filter(url => url !== null) as string[]));
  }

  public getPublicUploadS3PreSignedUrl(config) {
    return this.appSyncService.hydrated(true).pipe(
      switchMap(client =>
        client.query({
          query: gql(publicUploadS3PreSignedUrl),
          variables: config,
          fetchPolicy: 'network-only'
        })
      ),
      map((result: ApolloQueryResult<PublicUploadS3PreSignedUrlQuery>) => {
        return JSON.parse(result.data.publicUploadS3PreSignedUrl);
      })
    );
  }

  public getGetObjectS3PreSignedUrl(config) {
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.query({
          query: gql(getObjectS3PreSignedUrl),
          variables: { config },
          fetchPolicy: 'network-only'
        })
      ),
      map((result: ApolloQueryResult<GetObjectS3PreSignedUrlQuery>) => {
        return result.data.getObjectS3PreSignedUrl;
      })
    );
  }
}
